import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError, formatDate, EQUIPMENT_STATUSES, STAFF_ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, QrCode, Pencil, Trash2, Search, Loader2, Printer, PackageOpen } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { customer_id: "", name: "", manufacturer: "", model: "", serial_number: "", condition_on_receipt: "", remarks: "" };

export default function Equipment() {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user.role);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editStatus, setEditStatus] = useState("received");
  const [saving, setSaving] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);
  const [qrData, setQrData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/equipment", { params });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isStaff) api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, [isStaff]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ customer_id: e.customer_id, name: e.name, manufacturer: e.manufacturer, model: e.model, serial_number: e.serial_number, condition_on_receipt: e.condition_on_receipt, remarks: e.remarks || "" });
    setEditStatus(e.status);
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { customer_id, ...rest } = form;
        await api.put(`/equipment/${editing.id}`, { ...rest, status: editStatus });
        toast.success("Equipment updated");
      } else {
        await api.post("/equipment", form);
        toast.success("Equipment registered with work order");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete work order ${e.work_order_no}? This action is logged in the audit trail.`)) return;
    try {
      await api.delete(`/equipment/${e.id}`);
      toast.success("Equipment deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const showQr = async (e) => {
    setQrTarget(e);
    setQrData(null);
    try {
      const { data } = await api.get(`/equipment/${e.id}/qr`);
      setQrData(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="equipment-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Equipment Inward</h1>
          <p className="mt-1 text-sm text-muted-foreground">Registered devices with work orders and QR tracking.</p>
        </div>
        {isStaff && (
          <Button onClick={openCreate} data-testid="register-equipment-button">
            <Plus className="mr-2 h-4 w-4" /> Register Equipment
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, serial, work order…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="equipment-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="equipment-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(EQUIPMENT_STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border" data-testid="equipment-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Work Order</TableHead>
              <TableHead>Equipment</TableHead>
              <TableHead className="hidden md:table-cell">Manufacturer / Model</TableHead>
              <TableHead className="hidden lg:table-cell">Serial No.</TableHead>
              {isStaff && <TableHead className="hidden lg:table-cell">Customer</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="w-[130px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No equipment registered.</TableCell></TableRow>
            )}
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`equipment-row-${e.work_order_no}`}>
                <TableCell className="font-mono text-xs font-medium text-primary">{e.work_order_no}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <PackageOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{e.name}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">{[e.manufacturer, e.model].filter(Boolean).join(" / ") || "—"}</TableCell>
                <TableCell className="hidden font-mono text-xs lg:table-cell">{e.serial_number}</TableCell>
                {isStaff && <TableCell className="hidden text-sm lg:table-cell">{e.customer_name}</TableCell>}
                <TableCell><StatusBadge status={e.status} label={EQUIPMENT_STATUSES[e.status]} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => showQr(e)} data-testid={`qr-equipment-${e.work_order_no}`}><QrCode className="h-4 w-4" /></Button>
                  {isStaff && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-equipment-${e.work_order_no}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(e)} data-testid={`delete-equipment-${e.work_order_no}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="equipment-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${editing.work_order_no}` : "Register Incoming Equipment"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!editing && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="equipment-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Equipment Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Digital Pressure Gauge" data-testid="equipment-form-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Manufacturer</Label>
              <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="equipment-form-manufacturer-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Model</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="equipment-form-model-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Serial Number *</Label>
              <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="equipment-form-serial-input" />
            </div>
            {editing && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="equipment-form-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EQUIPMENT_STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Physical Condition on Receipt *</Label>
              <Textarea rows={2} value={form.condition_on_receipt} onChange={(e) => setForm({ ...form, condition_on_receipt: e.target.value })} placeholder="e.g. Good condition, accessories complete, no visible damage" data-testid="equipment-form-condition-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="equipment-form-remarks-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="equipment-form-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.serial_number || !form.condition_on_receipt || (!editing && !form.customer_id)} data-testid="equipment-form-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Save Changes" : "Register & Generate Work Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrTarget} onOpenChange={(o) => !o && setQrTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Asset QR Code</DialogTitle>
          </DialogHeader>
          {qrTarget && (
            <div className="flex flex-col items-center gap-3 py-2">
              {!qrData && <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />}
              {qrData && (
                <>
                  <img src={qrData.qr_base64} alt={`QR for ${qrTarget.work_order_no}`} className="h-48 w-48 rounded-md border border-border bg-white p-2" data-testid="qr-code-image" />
                  <div className="text-center">
                    <div className="font-mono text-sm font-semibold text-primary">{qrTarget.work_order_no}</div>
                    <div className="text-sm">{qrTarget.name}</div>
                    <div className="mt-1 break-all font-mono text-[10px] text-muted-foreground">{qrData.scan_url}</div>
                  </div>
                  <Link to={`/equipment/${qrTarget.id}/label`} target="_blank" className="w-full">
                    <Button className="w-full" data-testid="print-qr-label-link"><Printer className="mr-2 h-4 w-4" /> Print Asset Label</Button>
                  </Link>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
